import { QueryClient, useQuery } from "@tanstack/react-query";
import React, { createContext, ReactNode, useContext } from "react";
import { getActiveSubscription } from "../backend-services";
import {
  FEATURE_GATED_MODULES,
  ModuleCheckResult,
  ModuleName,
  ModuleStatus,
  SUBSCRIPTION_STATUS,
  SubscriptionFeatures,
  SubscriptionLimits,
  SubscriptionProviderProps,
  SubscriptionService,
  SubscriptionUsage,
  UNLIMITED_MODULES,
  UnlimitedModules,
  MODULES,
  SubscriptionGuardProps,
} from "../types/subscription";
import PadLock from "../assets/icons/PadLock";
import {
  LevelUpBanner,
  LevelUpInfo,
  ReachedLimitBanner,
  SubscriptionExpired,
} from "../components/SubscriptionBanner/SubscriptionUpgradeModal";
import SubscriptionPage from "../pages/Subscription";

const SubscriptionContext = createContext<SubscriptionService | null>(null);

// Custom hook to use subscription
export const useSubscription = (): SubscriptionService => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
};

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const [activeModal, setActiveModal] = React.useState<
    null | "limit" | "expired" | "levelUp"
  >(null);
  const [showModal, setShowModal] = React.useState(false);
  const [checkValid, setCheckValid] = React.useState(false);
  const showSubscriptionModal = (type: typeof activeModal) =>
    setActiveModal(type);
  const [moduleName, setModuleName] = React.useState<string | null>(null);
  const closeSubscriptionModal = () => setActiveModal(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["subscription"],
    queryFn: getActiveSubscription,
    // staleTime: 60_000, // 1 min—tweak for your UX
  });
  const handleSubscriptionTrigger = () => {
    setShowModal(true);
    setActiveModal(null);
  };
  const subscription = data;
  const subscriptionStatus = subscription?.["companyId#status"].split("#")[1];
  const hasSubscription = (): boolean => {
    return (
      subscription &&
      subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE &&
      subscription.plan !== "free"
    );
  };
  const isActive = (): boolean => {
    if (subscriptionStatus !== SUBSCRIPTION_STATUS.ACTIVE) return false;
    if (subscription?.paymentStatus !== "completed") return false;

    const now = new Date();
    const endDate = new Date(subscription.endDate);
    return now <= endDate;
  };

  const getModuleStatus = React.useCallback(
    (module: ModuleName) => {
      const rawLimit = subscription.limits[module] ?? -1;
      const limit = rawLimit === -1 ? Infinity : rawLimit; // ‼ -1 => unlimited
      const usage = subscription.usage[module] ?? 0;
      return {
        limit,
        usage,
        remaining: limit === Infinity ? Infinity : Math.max(0, limit - usage),
        isAtLimit: limit !== Infinity && usage >= limit,
        isOverLimit: limit !== Infinity && usage > limit,
        percentage: limit === Infinity ? 0 : (usage / limit) * 100,
      };
    },
    [subscription],
  );
  const hasFeature = React.useCallback(
    (f: keyof typeof subscription.limits) => subscription.limits[f] !== 0,
    [subscription],
  );

  const canUseModule = React.useCallback(
    (module: ModuleName, required = 1): ModuleCheckResult => {
      if (!isActive())
        return { allowed: false, reason: "subscription_inactive" };
      if (subscription.limits[module] === -1)
        return { allowed: true, reason: "allowed" };

      if (!hasFeature(module))
        return { allowed: false, reason: "feature_not_available" };

      const { usage, limit } = getModuleStatus(module);
      if (usage + required > limit)
        return { allowed: false, reason: "limit_exceeded" };

      return { allowed: true, reason: "allowed" };
    },
    [isActive, hasFeature, getModuleStatus],
  );

  const getUpgradeMessage = React.useCallback(
    (module: ModuleName): { reason: string; id: string } | null => {
      const { reason } = canUseModule(module);
      switch (reason) {
        case "limit_exceeded":
          return {
            reason: `You've reached your ${module} limit. Upgrade to continue.`,
            id: "limit_exceeded",
          };
        case "feature_not_available":
          return {
            id: "feature_not_available",
            reason: `${module} isn't available on your current plan.`,
          };
        case "subscription_inactive":
          return {
            id: "subscription_inactive",
            reason: "Your subscription is inactive. Please renew.",
          };
        default:
          return null;
      }
    },
    [canUseModule],
  );

  const getDaysRemaining = (): number => {
    const now = new Date();
    const endDate = new Date(subscription.endDate);
    const timeDiff = endDate.getTime() - now.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const triggerSubscriptionModal = (module: ModuleName, reason?: string) => {
    if (getUpgradeMessage(module)?.id == "limit_exceeded") {
      showSubscriptionModal("limit");
    } else if (getUpgradeMessage(module)?.id === "subscription_inactive") {
      showSubscriptionModal("expired");
    } else {
      showSubscriptionModal("levelUp");
    }
  };

  // Subscription Guard Component
  const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
    module,
    requiredCount = 1,
    children,
    fallback = null,
    showUpgradeMessage = true,
    showSecondaryText = true,
    isListPage = false,
    isInfo = false,
    createTitle = "Create",
    outlined = false,
    isMobileListPage = false,
    className = "",
  }) => {
    setModuleName(module);

    const moduleCheck = canUseModule(module, requiredCount);

    if (!moduleCheck.allowed) {
      setCheckValid(false);
      if (isListPage) {
        return (
          <button
            className={`font-semibold text-[16px] h-[48px] rounded-[4px]  p-3 flex items-center gap-2 lg:mr-3 ${
              outlined
                ? " bg-white text-[#044E97]"
                : "bg-[#044E97] text-[#ffffff]"
            } ${className}`}
            onClick={() => {
              if (getUpgradeMessage(module)?.id === "subscription_inactive") {
                showSubscriptionModal("expired");
              } else if (getUpgradeMessage(module)?.id === "limit_exceeded") {
                showSubscriptionModal("limit");
              } else {
                showSubscriptionModal("levelUp");
              }
            }}
          >
            <PadLock color={outlined ? "#044E97" : "white"} />
            {createTitle}
          </button>
        );
      }

      if (isInfo) {
        return (
          <LevelUpInfo
            onClose={closeSubscriptionModal}
            moduleName={createTitle}
            handleClick={handleSubscriptionTrigger}
          />
        );
      }

      if (isMobileListPage) {
        return (
          <button
            onClick={() => {
              if (getUpgradeMessage(module)?.id === "subscription_inactive") {
                showSubscriptionModal("expired");
              } else if (getUpgradeMessage(module)?.id === "limit_exceeded") {
                showSubscriptionModal("limit");
              } else {
                showSubscriptionModal("levelUp");
              }
            }}
            className="fixed bottom-16 right-8 bg-[#044E97] text-white text-[] rounded-full w-12 h-12 flex items-center justify-center shadow-lg"
            aria-label="Add"
          >
            <PadLock color="#ffffff" />
          </button>
        );
      }

      if (showUpgradeMessage) {
        return (
          <>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-800">
                <PadLock />
                <span className="font-medium">Upgrade Required</span>
              </div>
              {showSecondaryText && (
                <p className="text-sm text-yellow-700 mt-1">
                  {getUpgradeMessage(module)?.reason}
                </p>
              )}
            </div>
          </>
        );
      }
      setCheckValid(false);
      return <>{fallback}</>;
    }
    setCheckValid(true);
    return <>{children}</>;
  };

  const withSubscriptionCheck = <P extends object>(
    WrappedComponent: React.ComponentType<P & WithSubscriptionCheckProps>,
  ) => {
    return ({
      module,
      requiredCount = 1,
      showUpgradeMessage = true,
      ...props
    }: P & {
      module: ModuleName;
      requiredCount?: number;
      showUpgradeMessage?: boolean;
    }) => {
      const { canUseModule, getUpgradeMessage } = useSubscription();

      const moduleCheck = canUseModule(module, requiredCount);
      const upgradeMessage = getUpgradeMessage(module)?.reason || null;

      return (
        <WrappedComponent
          {...(props as P)}
          subscriptionCheck={moduleCheck}
          upgradeMessage={upgradeMessage}
          showUpgradeMessage={showUpgradeMessage}
        />
      );
    };
  };

  const service = React.useMemo<SubscriptionService>(
    () => ({
      subscription,
      status: "ready",
      refresh: refetch,
      isActive,
      hasFeature,
      getModuleStatus,
      canUseModule,
      getUpgradeMessage,
      MODULES,
      SUBSCRIPTION_STATUS,
      getDaysRemaining,
      SubscriptionGuard,
      withSubscriptionCheck,
      hasSubscription,
      checkValid,
      triggerSubscriptionModal,
    }),
    [
      subscription,
      refetch,
      isActive,
      hasFeature,
      getModuleStatus,
      canUseModule,
      getUpgradeMessage,
    ],
  );

  return (
    <SubscriptionContext.Provider value={service}>
      <>
        {children}
        {activeModal === "levelUp" && (
          <LevelUpBanner
            onClose={closeSubscriptionModal}
            moduleName={moduleName}
            handleClick={handleSubscriptionTrigger}
          />
        )}
        {activeModal === "limit" && (
          <ReachedLimitBanner
            onClose={closeSubscriptionModal}
            moduleName={moduleName}
            handleClick={handleSubscriptionTrigger}
          />
        )}
        {activeModal === "expired" && (
          <SubscriptionExpired
            onClose={closeSubscriptionModal}
            moduleName={moduleName}
            handleClick={handleSubscriptionTrigger}
          />
        )}
        {showModal && (
          <SubscriptionPage
            close={() => setShowModal(false)} // Close modal
          />
        )}
      </>
    </SubscriptionContext.Provider>
  );
};

export interface WithSubscriptionCheckProps {
  subscriptionCheck: ModuleCheckResult;
  upgradeMessage: string | null;
  showUpgradeMessage: boolean;
}

// Usage Display Component Props
export interface UsageDisplayProps {
  module: ModuleName;
  showPercentage?: boolean;
}
